// OrgSwitcher - pick the active organization + request a new one.
// Shows whenever the user has >=1 org (so single-org users can still request another).
import { useEffect, useState, useRef } from 'react';
import { ChevronDown, Check, Building2, Home, Plus, X, Send } from 'lucide-react';
import { myOrgs, switchOrg } from '../lib/orgApi.js';
import { requestOrg } from '../lib/orgRequestApi.js';

export default function OrgSwitcher() {
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(false);
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');
  const ref = useRef(null);

  useEffect(() => { myOrgs().then(({ data }) => setOrgs(data)); }, []);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Render if the user has at least one org (so they can request another).
  if (orgs.length < 1) return null;
  const active = orgs.find((o) => o.is_active) || orgs[0];

  /* G360_K460 - a refused switch left the menu disabled with no word
     why. It now says what failed and can be tried again. */
  async function choose(o) {
    if (o.is_active || busy) { setOpen(false); return; }
    setBusy(true); setErr('');
    const r = await switchOrg(o.tenant_id); // reloads on success
    if (r && r.error) { setBusy(false); setErr('Could not switch to ' + (o.name || 'that organization') + ': ' + (r.error.message || 'refused')); }
  }

  async function submitRequest() {
    setErr('');
    if (!name.trim()) { setErr('Organization name is required.'); return; }
    setSending(true);
    const { error } = await requestOrg(name.trim(), note.trim());
    setSending(false);
    if (error) { setErr(error.message); return; }
    setSent(true);
  }

  return (
    <div ref={ref} style={{ position: 'relative', margin: '8px 12px' }}>
      <button onClick={() => setOpen((v) => !v)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 10px', borderRadius: 8, border: '1px solid var(--g-border)',
          background: 'var(--g-surface)', cursor: 'pointer', fontSize: 13, textAlign: 'left' }}>
        <Building2 size={15} style={{ color: 'var(--g-text-2)', flexShrink: 0 }} />
        <span style={{ flex: 1, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {active?.name}
        </span>
        <ChevronDown size={15} style={{ color: 'var(--g-text-2)', flexShrink: 0 }} />
      </button>

      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 50,
          background: 'var(--g-surface)', border: '1px solid var(--g-border)', borderRadius: 8,
          boxShadow: 'var(--g-e2)', overflow: 'hidden', maxHeight: 360, overflowY: 'auto' }}>
          {orgs.length >= 2 && (
            <>
              <div style={{ fontSize: 11, color: 'var(--g-text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '8px 12px 4px' }}>
                Your organizations
              </div>
              {err && !sent && <div className="g360-org-switch-err" style={{ fontSize: 12, color: 'var(--g-danger)', padding: '4px 12px 8px' }}>{err}</div>}
              {orgs.map((o) => (
                <button key={o.tenant_id} onClick={() => choose(o)} disabled={busy}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                    padding: '9px 12px', border: 0, background: o.is_active ? 'var(--g-primary-50)' : 'transparent',
                    cursor: 'pointer', fontSize: 13, textAlign: 'left' }}>
                  <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {o.name}
                    <span style={{ color: 'var(--g-text-3)', fontSize: 11, marginLeft: 6 }}>{o.role}</span>
                    {o.is_home && <Home size={11} style={{ marginLeft: 6, color: 'var(--g-text-3)', verticalAlign: -1 }} />}
                  </span>
                  {o.is_active && <Check size={15} style={{ color: 'var(--g-primary)' }} />}
                </button>
              ))}
              <div style={{ borderTop: '1px solid var(--g-divider)' }} />
            </>
          )}
          <button onClick={() => { setOpen(false); setModal(true); setSent(false); setName(''); setNote(''); setErr(''); }}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 12px', border: 0, background: 'transparent', cursor: 'pointer',
              fontSize: 13, textAlign: 'left', color: 'var(--g-primary)' }}>
            <Plus size={15} />Request new organization
          </button>
        </div>
      )}

      {modal && (
        <div className="wiz-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setModal(false); }}>
          <div className="wiz-card" style={{ maxWidth: 420 }}>
            <button className="wiz-x" onClick={() => setModal(false)}><X size={18} /></button>
            {sent ? (
              <>
                <h2 className="wiz-h" style={{ textAlign: 'left', fontSize: 18 }}>Request submitted</h2>
                <p className="wiz-p" style={{ textAlign: 'left' }}>
                  Your request for <strong>{name}</strong> has been sent to the Govern360 team. Once approved,
                  it'll appear in your organization switcher.
                </p>
                <button className="btn primary block" style={{ marginTop: 12 }} onClick={() => setModal(false)}>Done</button>
              </>
            ) : (
              <>
                <h2 className="wiz-h" style={{ textAlign: 'left', fontSize: 18 }}>Request a new organization</h2>
                <p className="wiz-p" style={{ textAlign: 'left' }}>Our team reviews and approves new organizations. You'll become its owner once approved.</p>
                <label className="field-l">Organization name</label>
                <input className="inp" value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Inc." />
                <label className="field-l" style={{ marginTop: 12 }}>Note (optional)</label>
                <input className="inp" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything our team should know" />
                {err && <div className="banner danger" style={{ marginTop: 12, marginBottom: 0 }}>{err}</div>}
                <button className="btn primary block" style={{ marginTop: 16 }} onClick={submitRequest} disabled={sending || !name.trim()}>
                  {sending ? 'Sending\u2026' : <><Send size={16} />Submit request</>}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
